import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { AppService, Media } from '@lc/core';
import { ApiService } from '@re-core/ngx-ui';
import { firstValueFrom } from 'rxjs';
import { invariant } from '@lc/util';
import { produce } from 'immer';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AgentDetails,
  MarketingOrder,
  NewMarketingOrder,
  OpenHousesUpdate,
  Package,
  PackagePricing,
  VestigialMarketingOrderFields,
  VestigialProductFields,
} from '../../types';
import { GetOne, Post, Put } from '../../interfaces/api.interfaces';
import { ListingPhotoRequest } from '../../types/listing-photo';

function filterProperties(obj: any, ignoredProperties: string[]): any {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !ignoredProperties.includes(key)),
  ) as any;
}

const ignoredOrderProperties: Array<keyof VestigialMarketingOrderFields> = [
  'summary',
  'audit',
  'statusHistory',
  'pricing',
  'tasks',
  'ai',
];

function cleanOrder(order: MarketingOrder): MarketingOrder {
  return {
    ...filterProperties(order, ignoredOrderProperties),
    selectedPackage: cleanPackage(order.selectedPackage),
    availablePackages: order.availablePackages?.map(cleanPackage),
  };
}

const ignoredProductProperties: Array<keyof VestigialProductFields> = [
  'statusHistory',
  'publishConsents',
];

function cleanPackage(pkg?: Package) {
  if (!pkg) {
    return;
  }
  return {
    ...pkg,
    products: pkg.products.map((product) => filterProperties(product, ignoredProductProperties)),
  };
}

/**
 * Converts the order from the backend to a format that complies with the frontend type definitions.
 */
function convert(backendOrder: MarketingOrder): MarketingOrder {
  return produce(backendOrder, (draft) => {
    draft.selectedPackage?.products.forEach((product) => {
      if (!('serviceAppointment' in product)) return;

      const agency = product.serviceAppointment?.photoAgency;
      if (agency) {
        // The code gets converted to a number on the way back from the server
        agency.code = String(agency.code);
      }
    });
  });
}

@Injectable({ providedIn: 'root' })
export class MarketingOrderService
implements GetOne<MarketingOrder>, Post<MarketingOrder>, Put<MarketingOrder> {
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(ApiService);
  private readonly v1Url = `${AppService.get('persistBaseURL')}/marketing-orders`;
  private readonly v2Url = `${AppService.get('persistBaseURL').replace('v1', 'v2')}/orders`;

  readonly marketingOrder = signal<MarketingOrder | null>(null);

  async getOne(id: MarketingOrder['_id']) {
    const order = convert(
      await firstValueFrom(
        this.api
          .getOne$<MarketingOrder>(`${this.v2Url}/${id}`)
          .pipe(takeUntilDestroyed(this.destroyRef)),
      ),
    );
    this.marketingOrder.set(order);
    return order;
  }

  async post(marketingOrder: NewMarketingOrder) {
    const updatedOrder = convert(
      await this.api.post<MarketingOrder, NewMarketingOrder>(this.v2Url, marketingOrder),
    );
    this.marketingOrder.set(updatedOrder);
    return updatedOrder;
  }

  async put(marketingOrder: MarketingOrder) {
    const updatedOrder = convert(
      await this.api.put<MarketingOrder>(
        `${this.v2Url}/${marketingOrder._id}`,
        cleanOrder(marketingOrder),
      ),
    );
    this.marketingOrder.set(updatedOrder);
    return updatedOrder;
  }

  getPackagePricing(id: MarketingOrder['_id']): Promise<PackagePricing> {
    return firstValueFrom(this.api.getOne$<PackagePricing>(`${this.v2Url}/${id}/package-prices`));
  }

  async updateListingAgent(agentDetails: AgentDetails): Promise<MarketingOrder> {
    return this.updateOrder(async (marketingOrder) => {
      return convert(
        await this.api.put<MarketingOrder, AgentDetails>(
          `${this.v1Url}/${marketingOrder._id}/listing-agent`,
          agentDetails,
        ),
      );
    });
  }

  async submitOrder(stripeTokenId: string | null): Promise<void> {
    await this.updateOrder((order) => this.api.put<MarketingOrder, { stripeTokenId: string | null }>(
      `${this.v2Url}/${order._id}/submit`,
      {
        stripeTokenId,
      },
    ));
  }

  async addPhotos(photos: ListingPhotoRequest[]): Promise<void> {
    await this.updateOrder((marketingOrder) => {
      return this.api.put(`${this.v1Url}/${marketingOrder._id}/photos?push=true`, photos);
    });
  }

  async updateOpenHouses(update: OpenHousesUpdate): Promise<MarketingOrder> {
    return this.updateOrder((marketingOrder) => {
      return this.api.patch<MarketingOrder, OpenHousesUpdate>(
        `${this.v2Url}/${marketingOrder._id}/open-houses`,
        update,
      ).then(convert);
    });
  }

  /**
   * Update media on a product in an order
   * @param productCode the product code to update
   * @param media the media data
   */
  async updateMedia(productCode: string, media: Media) {
    await this.updateOrder((marketingOrder) => {
      return this.api.put(
        `${this.v1Url}/${marketingOrder._id}/media?productCode=${productCode}`,
        media,
      );
    });
  }

  async updateOrder(
    dispatch: (marketingOrder: MarketingOrder) => Promise<MarketingOrder>,
  ): Promise<MarketingOrder> {
    const order = this.marketingOrder();
    invariant(order, 'Marketing order must be defined.');
    const updatedOrder = await dispatch(order);
    this.marketingOrder.set(updatedOrder);
    return updatedOrder;
  }
}
